import { useState } from 'react';
import { compileAutomation, type Automation, type TryItBranchResult } from '@capture/shared';
import { formConfigApi } from '../../form-config/api/client';
import { SectionCard } from './SectionCard';
import styles from './panel.module.css';

export interface TryItSectionProps {
  /** The in-progress (possibly unsaved) automation draft — compiled at click
   *  time so Try-it always reflects the latest edits, saved or not. */
  automation: Automation;
  /** The scoped form's field names — same list `ScopeSection` uses for its
   *  field picker. Seeds one blank sample-value input per field. */
  formFields: Array<{ name: string; label: string }>;
  /** Pipeline stage to evaluate at (drives `stages[]`/legacy-`stage` gating
   *  the same way a real request would). Defaults to 'verification'. */
  stage?: string;
  /** Draft version id — Try-it posts to
   *  `/job-type-versions/:versionId/automation-test-run`. `null` while the
   *  draft hasn't loaded yet; Run is disabled in that case. */
  versionId: string | null;
}

/**
 * Try-it (Task 12) — dry-runs the current, possibly-unsaved automation draft
 * against operator-supplied sample field values. Compiles client-side
 * (`compileAutomation`) and POSTs to the server evaluator; nothing is saved.
 * Mirrors `TestPanel.tsx`'s layout (field inputs seeded blank, a Run button,
 * per-result rows below).
 */
export function TryItSection({ automation, formFields, stage = 'verification', versionId }: TryItSectionProps) {
  const [fields, setFields] = useState<Record<string, string>>(() => {
    const seed: Record<string, string> = {};
    for (const f of formFields) seed[f.name] = '';
    return seed;
  });
  const [branches, setBranches] = useState<TryItBranchResult[] | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function setField(name: string, value: string) {
    setFields((prev) => ({ ...prev, [name]: value }));
  }

  async function runHandler() {
    if (!versionId) return;
    setRunning(true);
    setError(null);
    setBranches(null);
    try {
      const rules = compileAutomation(automation);
      const res = await formConfigApi.automationTestRun(versionId, { fields, stage, rules });
      setBranches(res.branches);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRunning(false);
    }
  }

  const firedCount = branches?.filter((b) => b.fired).length ?? 0;
  const summary =
    branches === null
      ? 'Not run yet'
      : `${firedCount} of ${branches.length} branch${branches.length === 1 ? '' : 'es'} fired`;

  return (
    <SectionCard title="Try it" summary={summary} defaultOpen>
      <p className="rb-hint">
        Dry-run this automation — including unsaved edits — against sample field values. Nothing
        is saved.
      </p>

      <div className={styles.tryItFields}>
        {formFields.map((f) => (
          <div key={f.name} className={styles.formGrid}>
            <label className={styles.fieldLabel} htmlFor={`try-it-field-${f.name}`}>
              {f.label}
            </label>
            <input
              id={`try-it-field-${f.name}`}
              className={styles.textInput}
              type="text"
              value={fields[f.name] ?? ''}
              onChange={(e) => setField(f.name, e.target.value)}
              placeholder="Sample value"
            />
          </div>
        ))}
      </div>

      <button
        type="button"
        className={styles.tryItRunBtn}
        onClick={runHandler}
        disabled={running || !versionId}
      >
        {running ? 'Running…' : 'Run'}
      </button>

      {error && <p className={styles.tryItError}>{error}</p>}

      {branches && (
        <div className={styles.tryItResults}>
          {branches.map((b) => (
            <div key={b.index} className={styles.tryItBranchRow} data-fired={b.fired}>
              <span className={styles.tryItBranchIndex}>Branch {b.index + 1}</span>
              <span className={styles.tryItOutcome}>
                {/* Webhook's `outcome` is now a descriptive "would call <url>
                    with fields: …" report (S-5 T8, design §5) — show it
                    directly rather than swallowing it behind the generic
                    skip message. Other skip reasons (e.g. custom-script)
                    keep the generic message since their outcome text isn't
                    descriptive. */}
                {b.skipped && b.skipped !== 'webhook'
                  ? `Skipped (${b.skipped}) — not simulatable`
                  : b.outcome}
              </span>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}
